/* eslint-disable ember/no-runloop */
import { set } from '@ember/object';
import { begin, end } from '@ember/runloop';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismIcon } from '@mylighthouse/prism-ember';
import GeneralChartsOtaChart from 'lumen/components/general/charts/ota-chart';
import HotelMarketInsightTooltipsTooltipSearches from 'lumen/components/hotel/market-insight/tooltips/tooltip-searches';
import {
  getWeekendPlotBands,
  tickPositionerDayWeek,
} from 'lumen/utils/charts/chart-utils';
import {
  MI_DEFAULT_DATE_FORMAT,
  MI_SEARCHES_TYPE_FLIGHTS_BUSINESS,
  MI_SEARCHES_TYPE_FLIGHTS_LEISURE,
  MI_SEARCHES_TYPE_HOTELS_BUSINESS,
  MI_SEARCHES_TYPE_HOTELS_LEISURE,
} from 'lumen/utils/constants/general-constants';
import {
  DETAIL_PANE_DETAIL_SECTION_CARD_IDS,
  MI_OVERVIEW_COLUMN_CONFIG,
} from 'lumen/utils/constants/market-insight-constants';
import { xAxisLabelFormatterForOverview } from 'lumen/utils/market-insight/market-insight-helpers';

const SEARCHES_SERIES = 'searches';

interface HotelMarketInsightChartsChartSearchesOverviewSignature {
  Args: {
    onChartClicked: unknown;
    onUpdateChartReference: unknown;
    searchsummaries: unknown;
  };
}

export default class HotelMarketInsightChartsChartSearchesOverview extends Component<HotelMarketInsightChartsChartSearchesOverviewSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service declare miSelections: Services['mi-selections'];
  // eslint-disable-next-line ember/no-unused-services
  @service declare usageAnalytics: Services['usage-analytics'];

  @tracked hoveredSearchSummary = null;
  // @ts-expect-error: Incorrect type
  @tracked isVisible;
  @tracked tooltipDay = null;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get chartData() {
    const data = [];

    data.push({
      type: 'column',
      name: SEARCHES_SERIES,
      data: this.formattedChartData,
    });

    return data;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get chartOptions() {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const that = this;

    return {
      chart: {
        height: 190,
        marginLeft: 63, // Align the searches chart bars with the demand and otb chart bars
        zoomType: null,
      },
      title: {
        text: SEARCHES_SERIES, // title used for the events only
      },
      xAxis: {
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        tickPositioner() {
          return tickPositionerDayWeek(
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            this.dataMin,
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            this.dataMax,
            that.miSelections.isDailyOverview,
          );
        },
        labels: {
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
          formatter() {
            const formattedDate = xAxisLabelFormatterForOverview(
              // @ts-expect-error: Incorrect type
              // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
              this.value,
              that.miSelections.isDailyOverview,
              that.intl,
            );

            const warningIcon =
              // @ts-expect-error: Incorrect type
              document.getElementById('warning-icon').innerHTML;
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            const showWarningIcon = this.isFirst && that.isFirstWeekIncomplete;
            // @ts-expect-error: Incorrect type
            const warningSign = showWarningIcon ? warningIcon : '';

            return [
              `<div class="x-axis-label-with-icon">`,
              `  <div>${formattedDate}</div>`,
              `  ${warningSign}`,
              `</div>`,
            ].join('\n');
          },
          useHTML: true,
        },
        plotBands: this.weekendPlotBands,
        crosshair: true,
      },
      yAxis: {
        labels: {
          enabled: false,
        },
        title: {
          enabled: false,
        },
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        softMax:
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          that.sortedAndFilteredSearchSummaries[0]?.numSearchesUpperBound,
        gridLineColor: 'transparent', // We set plotlines already
        endOnTick: false, // Needed to put this one and the next one on false to prevent highcharts adding a margin
        startOnTick: false,
      },
      tooltip: {
        hideDelay: 0.2,
        padding: 0,
        borderWidth: 0,
        shadow: false,
        outside: true, // allows the tooltip to render outside the chart's SVG element box (required because else the chart tooltip can get cut off)
        shape: 'square',
        distance: 20,
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        formatter() {
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          const { day, searchSummary } = this.point;

          // Necessary to get correct data for tooltips, needs to be in separate runloop
          begin();
          set(that, 'tooltipDay', day);
          set(that, 'hoveredSearchSummary', searchSummary);
          end();
          // @ts-expect-error: Incorrect type
          return document.getElementById('dummy-tooltip-for-searches')
            .innerHTML;
        },
        useHTML: true,
      },
      legend: {
        enabled: false,
      },
      plotOptions: {
        column: MI_OVERVIEW_COLUMN_CONFIG,
        series: {
          cursor: 'pointer',
          events: {
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
            click(e) {
              // @ts-expect-error: Incorrect type
              that.args.onChartClicked?.(
                e,
                DETAIL_PANE_DETAIL_SECTION_CARD_IDS.SEARCHES,
              );

              if (that.miSelections.isDailyOverview) {
                const dateClicked = that.hotelAndMarketService.toLocalTime(
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
                  e.point.x,
                );
                that.usageAnalytics.track('Daily Market Demand Viewed', {
                  eventTrigger: 'search-traffic-chart',
                  daysOffsetFromToday: dateClicked
                    .startOf('day')
                    .diff(
                      that.hotelAndMarketService.getLocalTime().startOf('day'),
                      'days',
                    ),
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                  isOutlier: e.point.day.outliers.length > 0,
                });
              }
            },
          },
        },
      },
    };
  }

  /**
   * This return the data for each of the 3 series we show in the chart
   */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedChartData() {
    // @ts-expect-error: Incorrect type
    const demandData = [];

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    this.sortedAndFilteredSearchSummaries.forEach((searchSummary) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const { day } = searchSummary;
      const formattedDay = this.hotelAndMarketService.toLocalUnixTime(
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        day.date.unix(),
      );

      demandData.push({
        x: formattedDay,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        y: searchSummary.numSearches,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        color: searchSummary.demandLevelColor,
        // We pass the day and the searchSummary for the tooltip
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        day,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        searchSummary,
      });
    });

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return demandData;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isFirstWeekIncomplete() {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
    return this.sortedAndFilteredSearchSummaries[0].isSummaryIncomplete;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get searchesTooltipTitle() {
    if (this.miSelections.isWeeklyOverview) {
      return this.intl.t('general.week_of', {
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        date: this.tooltipDay?.date.format(MI_DEFAULT_DATE_FORMAT),
      });
    }
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
    return this.tooltipDay?.date.format(
      this.miSelections.defaultDateFormatWithDOW,
    );
  }

  /**
   * We only need the latest summaries for the chart
   * We also sort them by day since it is required for highcharts
   */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get sortedAndFilteredSearchSummaries() {
    let searchesType = MI_SEARCHES_TYPE_FLIGHTS_LEISURE;
    if (this.miSelections.isFlightBusinessSearchesTabActive) {
      searchesType = MI_SEARCHES_TYPE_FLIGHTS_BUSINESS;
    }
    if (this.miSelections.isHotelSearchesTabActive) {
      searchesType = MI_SEARCHES_TYPE_HOTELS_LEISURE;
    }
    if (this.miSelections.isHotelBusinessSearchesTabActive) {
      searchesType = MI_SEARCHES_TYPE_HOTELS_BUSINESS;
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return (
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      this.args.searchsummaries
        .filter(
          // @ts-expect-error: Incorrect type
          (searchsummary) =>
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
            searchsummary.isLatest && searchsummary.type === searchesType,
        )
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        .sort((a, b) => a.day.integerDate - b.day.integerDate)
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get weekendPlotBands() {
    if (this.miSelections.isDailyOverview) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const summaries = this.sortedAndFilteredSearchSummaries ?? [];
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      const startDate = summaries[0]?.day?.date;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      const endDate = summaries[summaries.length - 1]?.day?.date;

      return getWeekendPlotBands(
        this.hotelAndMarketService,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        startDate,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        endDate,
      );
    }

    return [];
  }

  <template>
    <GeneralChartsOtaChart
      {{! @glint-expect-error: Incorrect type }}
      @chartOptions={{this.chartOptions}}
      @model={{this.chartData}}
      {{! @glint-expect-error: Incorrect type }}
      @onUpdateChartReference={{@onUpdateChartReference}}
    />

    <div class="hidden" id="dummy-tooltip-for-searches">
      <div class="tooltip-market-insight default-tooltip">
        <HotelMarketInsightTooltipsTooltipSearches
          @searchSummary={{this.hoveredSearchSummary}}
          @tooltipTitle={{this.searchesTooltipTitle}}
        />
      </div>
    </div>
    <div class="hidden" id="warning-icon">
      <PrismIcon @color="warning" @name="warning" @size="extra-small" />
    </div>
  </template>
}
